import React, { useEffect, useRef, useState } from 'react';
import './DatePickerPopover.css';
import { logInteraction } from '../Dashboard/sessionEventPush';

// ── Helpers ─────────────────────────────────────────────────────────────────
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function todayYMD() { return toYMD(new Date()); }

/**
 * DatePickerPopover — IOO-324
 *
 * Props:
 *   open            {boolean}           controlled visibility
 *   onClose         {function}          called when Done or outside click
 *   chosenDates     {Set<string>}       YYYY-MM-DD strings currently selected
 *   maxDates        {number}            max selectable dates (default 2)
 *   availableDates  {string[]}          YYYY-MM-DD strings that are bookable
 *                                       (empty array = all dates bookable)
 *   onDateToggle    {function(string)}  called with a YYYY-MM-DD when cell clicked
 *   onClearAll      {function}          called when "Clear all" pressed
 *   maxDaysAhead    {number}            how many days ahead are selectable (default 30)
 */
const DatePickerPopover = ({
  open,
  onClose,
  chosenDates = new Set(),
  maxDates = 2,
  availableDates = [],   // empty = all weekdays bookable
  onDateToggle,
  onClearAll,
  maxDaysAhead = 30,
}) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [viewYear, setViewYear]   = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth()); // 0-indexed
  const popoverRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  if (!open) return null;

  // Compute latest bookable date
  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + maxDaysAhead);

  // Navigate months
  const changeMonth = (delta) => {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m > 11) { m = 0;  y++; }
    if (m < 0)  { m = 11; y--; }
    logInteraction("date_picker_month_navigated", { direction: delta > 0 ? "next" : "previous", month: m, year: y });
    setViewMonth(m);
    setViewYear(y);
  };

  // Build calendar cells
  const firstDay = new Date(viewYear, viewMonth, 1);
  // Mon=0 … Sun=6
  const startOffset = (firstDay.getDay() + 6) % 7; // shift Sun(0) to end
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [];

  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(viewYear, viewMonth, d);
    const ymd  = toYMD(date);
    cells.push({ d, ymd, date });
  }

  const capReached = chosenDates.size >= maxDates;
  const todayStr   = todayYMD();
  const maxStr     = toYMD(maxDate);

  const isDisabled = (cell) => {
    if (!cell) return true;
    // Past
    if (cell.ymd < todayStr) return true;
    // Beyond max
    if (cell.ymd > maxStr) return true;
    // Has specific available dates list and this date isn't in it — clinics can
    // have real weekend availability (confirmed by is_weekend:true slots in the
    // API response), so weekends must not be blanket-disabled here; whether a
    // date is bookable is entirely determined by availableDates below.
    if (availableDates.length > 0 && !availableDates.includes(cell.ymd)) return true;
    // Cap reached and not already selected
    if (capReached && !chosenDates.has(cell.ymd)) return true;
    return false;
  };

  const handleCellClick = (cell) => {
    if (!cell || isDisabled(cell)) return;
    onDateToggle?.(cell.ymd);
  };

  return (
    <div className="dp-popover" ref={popoverRef}>
      {/* Month navigation */}
      <div className="dp-month-nav">
        <button onClick={() => changeMonth(-1)} aria-label="Previous month">‹</button>
        <span className="dp-month-label">{MONTH_NAMES[viewMonth]} {viewYear}</span>
        <button onClick={() => changeMonth(1)} aria-label="Next month">›</button>
      </div>

      {/* Day headers Mon–Sun */}
      <div className="dp-grid-header">
        {DAY_NAMES.map(d => (
          <div key={d} className="dp-day-hdr">{d}</div>
        ))}
      </div>

      {/* Grid */}
      <div className="dp-grid">
        {cells.map((cell, idx) => {
          if (!cell) return <div key={`empty-${idx}`} className="dp-cell dp-cell--empty" />;
          const selected  = chosenDates.has(cell.ymd);
          const disabled  = isDisabled(cell);
          const isToday   = cell.ymd === todayStr;
          const capBlock  = capReached && !selected && !disabled;

          const classes = [
            'dp-cell',
            disabled  ? 'dp-cell--disabled' : '',
            selected  ? 'dp-cell--selected' : '',
            isToday   ? 'dp-cell--today'    : '',
            capBlock  ? 'dp-cell--cap-reached' : '',
          ].filter(Boolean).join(' ');

          return (
            <div
              key={cell.ymd}
              className={classes}
              title={selected ? 'Click to deselect' : disabled ? '' : 'Click to select'}
              onClick={() => handleCellClick(cell)}
            >
              {cell.d}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="dp-footer">
        <span className="dp-footer-note">
          {chosenDates.size}/{maxDates} selected
        </span>
        <div className="dp-footer-actions">
          <button className="dp-btn-clear" onClick={onClearAll}>Clear all</button>
          <button className="dp-btn-done" onClick={() => { logInteraction("date_picker_done_clicked", { selected_count: chosenDates.size }); onClose?.(); }}>Done</button>
        </div>
      </div>
    </div>
  );
};

export default DatePickerPopover;
